import {
  createElement,
  CSSProperties,
  forwardRef,
  HTMLProps,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { classNames } from "@/utils/classNames";
import {
  gridColsMapping,
  gridColsMappingLg,
  gridColsMappingMd,
  gridColsMappingSm,
  gridColsMappingXl,
  gridColSpanMapping,
  gridColSpanMappingLg,
  gridColSpanMappingMd,
  gridColSpanMappingSm,
  gridColSpanMappingXl,
  gridColSpanMappingXxl,
} from "./tailwindMappings";
import { IconName } from "./iconMapping";
import { IconProps } from "./Icon";
import { renderIcon } from "./iconHelpers";
import { isReactElement, mergeRefs } from "@/utils/reactHelpers";
import { boxClassNames, BoxProps, CommonProps, commonPropsToClassName } from "./boxHelpers";
import { TMessage } from "@/i18n/tt";
import { useTranslation } from "@/stores/RootStoreContext";
import { clamp } from "lodash";

export type VBoxProps = Omit<BoxProps, "vertical">;

export type ContainerProps = VBoxProps;

export type GridProps = CommonProps & {
  children?: ReactNode;
  cols?: number;
  colsSm?: GridProps["cols"];
  colsMd?: GridProps["cols"];
  colsLg?: GridProps["cols"];
  colsXl?: GridProps["cols"];
  inline?: boolean;
  htmlProps?: Omit<HTMLProps<HTMLDivElement>, "className">;
};

type GridItemColSpan = "full" | number;

export type GridItemProps = CommonProps & {
  children?: ReactNode;
  colSpan?: GridItemColSpan;
  xs?: GridItemColSpan;
  sm?: GridItemColSpan;
  md?: GridItemColSpan;
  lg?: GridItemColSpan;
  xl?: GridItemColSpan;
  xxl?: GridItemColSpan;
};

export type CardProps = BoxProps & {
  icon?: IconName | IconProps;
  title?: string | ReactElement | TMessage;
  subTitle?: string | ReactElement | TMessage;
  action?: ReactElement;
};

function getScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
  el = el?.parentElement || null;
  while (el) {
    const style = getComputedStyle(el);
    const overflowY = style.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Flex container for horizontal layout. The primary building block for composing UI.
 *
 * Lays out children **horizontally** by default with a small gap between them.
 * Use props for spacing (`gap`, `padding`, `margin`), colors (`bg`, `fg`),
 * borders, text styling, shadows, and more — prefer these over CSS classes.
 *
 * @example
 * ```tsx
 * <Box gap="md" padding="lg" bg="base" alignItems="center">
 *   <Icon name="info" />
 *   <span>Horizontal row</span>
 * </Box>
 * ```
 *
 * @see {@link VBox} for vertical stacking (equivalent to `<Box vertical>`).
 */
export const Box = forwardRef<HTMLDivElement, BoxProps>(function Box(props, ref) {
  const { t } = useTranslation();
  const internalRef = useRef<HTMLDivElement>(null);
  const { children, htmlProps, testId, testType, tooltip, sticky, onPinnedChange } = props;
  const [isStuck, setIsStuck] = useState(false);
  const [offset, setOffset] = useState(0);
  const offsetStyles: CSSProperties | undefined =
    sticky === "bottom" ? { bottom: -offset } : sticky ? { top: -offset } : undefined;

  useEffect(() => {
    if (sticky && internalRef.current) {
      const container = getScrollableAncestor(internalRef.current);
      if (container) {
        const s = getComputedStyle(container);
        const m =
          sticky === "bottom"
            ? parseInt(s.paddingBottom) + parseInt(s.borderBottom) + parseInt(s.marginBottom)
            : parseInt(s.paddingTop) + parseInt(s.borderTop) + parseInt(s.marginTop);
        const observer = new IntersectionObserver(
          ([e]) => {
            onPinnedChange?.(e.intersectionRatio < 1);
            setIsStuck(e.intersectionRatio < 1);
          },
          {
            threshold: [1],
            root: container,
            rootMargin: sticky === "bottom" ? "0px 0px -1px 0px" : "-1px 0px 0px 0px",
          },
        );
        observer.observe(internalRef.current);
        setOffset(m);
        return () => {
          observer.disconnect();
        };
      }
    }
  }, [onPinnedChange, sticky]);

  return createElement(props.element ?? "div", {
    ref: mergeRefs(ref, internalRef),
    style: offsetStyles,
    className: classNames(boxClassNames(props), isStuck && props.stuckClassName),
    ...htmlProps,
    "data-testid": testId,
    "data-test-type": testType,
    "data-tooltip": typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined,
    children,
  });
});

/**
 * Flex container for vertical layout. Shorthand for `<Box vertical>`.
 *
 * Stacks children **vertically** with a small gap between them by default.
 * Accepts the same props as Box (spacing, colors, borders, text, etc.)
 * except `vertical` which is always `true`.
 *
 * @example
 * ```tsx
 * <VBox gap="md" padding="lg">
 *   <h2>Title</h2>
 *   <p>Description</p>
 *   <Button label="Action" variant="primary" />
 * </VBox>
 * ```
 *
 * @see {@link Box} for horizontal layout.
 */
export const VBox = forwardRef<HTMLDivElement, VBoxProps>(function VBox(props, ref) {
  return <Box ref={ref} {...props} vertical={true} />;
});

export const Container = forwardRef<HTMLDivElement, ContainerProps>(function Container(
  { container, ...props },
  ref,
) {
  return <VBox ref={ref} container={container ?? true} {...props} />;
});

const clampCols = (cols: number) => clamp(cols, 1, 12);
const clampColSpan = (cols: number | "full") => (cols === "full" ? cols : clamp(cols, 1, 12));

export const Grid = forwardRef<HTMLDivElement, GridProps>(function Grid(
  { children, cols, colsSm, colsMd, colsLg, colsXl, inline, testId, tooltip, htmlProps, ...props },
  ref,
) {
  const { t } = useTranslation();
  return (
    <div
      ref={ref}
      className={classNames(
        commonPropsToClassName(props),
        gridColsMapping[clampCols(cols ?? 12)],
        colsSm && gridColsMappingSm[clampCols(colsSm)],
        colsMd && gridColsMappingMd[clampCols(colsMd)],
        colsLg && gridColsMappingLg[clampCols(colsLg)],
        colsXl && gridColsMappingXl[clampCols(colsXl)],
        inline ? "inline-grid" : "grid",
      )}
      data-testid={testId}
      data-tooltip={typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined}
      {...htmlProps}
    >
      {children}
    </div>
  );
});

export const GridItem = forwardRef<HTMLDivElement, GridItemProps>(function GridItem(
  { children, colSpan, xs, sm, md, lg, xl, xxl, tooltip, ...props },
  ref,
) {
  const { t } = useTranslation();
  return (
    <div
      ref={ref}
      className={classNames(
        commonPropsToClassName(props),
        colSpan && gridColSpanMapping[clampColSpan(colSpan)],
        xs && gridColSpanMapping[clampColSpan(xs)],
        sm && gridColSpanMappingSm[clampColSpan(sm)],
        md && gridColSpanMappingMd[clampColSpan(md)],
        lg && gridColSpanMappingLg[clampColSpan(lg)],
        xl && gridColSpanMappingXl[clampColSpan(xl)],
        xxl && gridColSpanMappingXxl[clampColSpan(xxl)],
      )}
      data-tooltip={typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined}
    >
      {children}
    </div>
  );
});

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { icon, title, subTitle, action, children, ...props },
  ref,
) {
  const { t } = useTranslation();
  return (
    <Box ref={ref} rounded padding bg dropShadow vertical {...props} testType={"Card"}>
      {(title || action) && (
        <VBox>
          <Box alignItems={"center"} justifyContent={"space-between"}>
            <h5 className={"items-center flex gap-1"}>
              {icon && renderIcon(icon)}
              {typeof title === "string" || isReactElement(title) || !title ? title : t(title)}
            </h5>
            {action}
          </Box>
          {subTitle && (
            <div>
              {typeof subTitle === "string" || isReactElement(subTitle) || !subTitle
                ? subTitle
                : t(subTitle)}
            </div>
          )}
        </VBox>
      )}
      {children}
    </Box>
  );
});

/**
 * A spacer to ensure that the bottom-most components can be scrolled passed the "Chat" buttons
 */
export const BottomSpacer = forwardRef<HTMLDivElement>(function BottomSpacer(
  { children }: { children?: React.ReactNode },
  ref,
) {
  return (
    <div ref={ref} style={{ height: "4rem", flex: "none" }}>
      {children}
    </div>
  );
});
